import { isUndefined } from 'lodash-es';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useIntl } from 'react-intl';

import { DownloadDataButton } from './DownloadDataButton.js';
import { ExploreButton } from './ExploreButton.js';
import { PanButton } from './PanButton.js';
import { ResetButton } from './ResetButton.js';
import { ZoomInOutButtons } from './ZoomInOutButtons.js';
import { ZoomModeButton } from './ZoomModeButton.js';
import { useBehavioralTrackingProps as _useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import type { ChartToolbarRendererProps } from '../../../core/components/toolbar/types.js';
import { getMenuTriggerDisplayMode } from '../../../core/components/toolbar/utils/get-menu-trigger-display-mode.js';
import { getMenuTriggerIcon } from '../../../core/components/toolbar/utils/get-menu-trigger-icon.js';
import { ChartOverlaysElevation } from '../../../core/constants.js';
import { getA11yToolbarConfig } from '../../../core/utils/zoom.js';
import { useInteractionModeState } from '../../hooks/store/selectors.js';
import { useChartInteractions } from '../../hooks/useChartInteractions.js';
import { useChartToolbar } from '../../hooks/useChartToolbar.js';
import { useTimeseriesRawData } from '../../hooks/useTimeseriesRawData.js';
import { useTimeseriesSelectionAreaRef } from '../../hooks/useTimeseriesSelectionAreaRef.js';
import { TIMESERIES_MESSAGES } from '../../messages/tooltips-text.js';
import { buildAndDownloadCsv } from '../../utils/download/build-and-download-csv.js';

const defaultToolbarPlacement = 'top-right';

type TimeseriesChartToolbarProps = {
  onMouseEnter?: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  onMouseLeave?: (event?: ReactMouseEvent<HTMLDivElement, MouseEvent>) => void;
  handlerRef?: ChartToolbarRendererProps['handlerRef'];
};

export const TimeseriesChartToolbar = ({
  onMouseEnter,
  onMouseLeave,
  handlerRef,
}: TimeseriesChartToolbarProps) => {
  const selectionAreaRef = useTimeseriesSelectionAreaRef();

  const config = useChartToolbar();
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  const { zoom, zoomX, pan } = useChartInteractions();
  const intl = useIntl();
  const isDownloadEnabled = config?.downloadData?.enabled ?? false;
  const isPanEnabled = pan?.enabled;
  const isZoomEnabled = zoom?.enabled;
  const isZoomXEnabled = zoomX?.enabled;
  const anyInteractionEnabled =
    isZoomEnabled ||
    isZoomXEnabled ||
    isPanEnabled ||
    isDownloadEnabled ||
    config?.intents;

  const mode = useInteractionModeState();
  const menuIcon = getMenuTriggerIcon(mode, isZoomEnabled);

  const menuLabel =
    !isPanEnabled && !isZoomEnabled
      ? intl.formatMessage(TIMESERIES_MESSAGES.zoomModeExplore)
      : undefined;

  const rawData = useTimeseriesRawData();

  /** Handles the download button when clicked. */
  const handleDownloadDataToolbar = () => buildAndDownloadCsv(rawData);

  const menuDisplayMode = getMenuTriggerDisplayMode({
    isPanEnabled,
    isZoomEnabled,
    isDownloadEnabled,
  });

  const onClose = () =>
    selectionAreaRef ? selectionAreaRef.current?.focus() : undefined;

  const showDefaultToolbarDownloadButton =
    isDownloadEnabled && !config?.intents;
  const showMenuToolbarButton = isDownloadEnabled && !!config?.intents?.length;

  const behaviorTrackingProps = _useBehavioralTrackingProps(config ?? {})[0];

  return !isUndefined(config) && anyInteractionEnabled ? (
    <ChartToolbarRenderer
      handlerRef={handlerRef}
      visibleWhenMouseInBounds
      collapsed={config.collapsed}
      draggable={true}
      placement={config.placement || defaultToolbarPlacement}
      initialA11yState={getA11yToolbarConfig({
        zoomX,
        pan,
        zoom,
        download: config.downloadData,
        intent: !!config.intents,
      })}
      zIndex={ChartOverlaysElevation.toolbar}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      menuIcon={menuIcon}
      menuTriggerLabel={menuLabel}
      menuDisplayMode={menuDisplayMode}
      onClose={onClose}
      isDownloadEnabled={showMenuToolbarButton}
      intents={config?.intents}
      handleDownloadDataToolbar={handleDownloadDataToolbar}
      {...behaviorTrackingProps}
    >
      <>
        <ExploreButton />
        {isZoomEnabled && <ZoomModeButton />}
        {isPanEnabled && <PanButton />}
      </>
      {isZoomEnabled && <ZoomInOutButtons />}
      {isZoomEnabled && <ResetButton />}
      {showDefaultToolbarDownloadButton && <DownloadDataButton />}
    </ChartToolbarRenderer>
  ) : null;
};

TimeseriesChartToolbar['displayName'] = 'ToolbarChartToolbar';
